import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  signal,
  ViewChild,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { RecaptchaModule } from 'ng-recaptcha';

import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';

import { Auth } from 'src/providers/auth/auth';
import { Client } from 'src/providers/client/client';
import { Me } from 'src/providers/me/me';
import { Events } from 'src/providers/events/events';
import { Card } from 'src/providers/card/card';
import { StripeProvider } from 'src/providers/stripe/stripe-provider';

import { SharedLinkService } from 'src/shared/services/shared-link.service';
import { GuestAccountService } from 'src/shared/services/guest-account.service';
import { PosthogService } from 'src/shared/services/posthog.service';
import { BookService } from 'src/shared/services/book.service';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { validateEmail } from 'src/shared/validator/validateEmail';

import { SharedLinkResponse } from 'src/models/shared-link.model';

@Component({
  selector: 'inline-payment-form',
  standalone: true,
  imports: [ReactiveFormsModule, RecaptchaModule, TidyUiComponentsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (!success && loaded) {
      @if (isGuestMode) {
        <form [formGroup]="accountForm" class="inline-account-form">
          <div class="inline-form-row">
            <tidy-input
              formControlName="firstName"
              [form]="accountForm.controls.firstName"
              [label]="'First Name'"
              [errorMessage]="'Please enter your first name.'"
              [submitted]="submitted"
              [icon]="'assets/svg/person.svg'" />
            <tidy-input
              formControlName="lastName"
              [form]="accountForm.controls.lastName"
              [label]="'Last Name'"
              [errorMessage]="'Please enter your last name.'"
              [submitted]="submitted"
              [icon]="'assets/svg/person.svg'" />
          </div>
          <tidy-input
            formControlName="email"
            [form]="accountForm.controls.email"
            [label]="'Email'"
            [type]="'email'"
            [autoComplete]="'email'"
            [errorMessage]="'Please enter a valid email.'"
            [submitted]="submitted"
            [icon]="'assets/svg/mail.svg'" />
          <tidy-input
            formControlName="password"
            [form]="accountForm.controls.password"
            [label]="'Password'"
            [type]="'password'"
            [autoComplete]="'new-password'"
            [errorMessage]="'Password must be at least 8 characters.'"
            [submitted]="submitted"
            [icon]="'assets/svg/lock-closed.svg'" />
        </form>
      }
      <div class="inline-payment-header">Enter Card Information</div>
      @if (!stripeReady()) {
        <div class="form-row">
          <ion-skeleton-text animated style="width: 100%; height: 22px; margin: 0;" />
        </div>
      }
      <div #cardInfo class="inline-card-input" [class.stripe-loading]="!stripeReady()"></div>
      @if (errorMessage) {
        <div class="inline-payment-error">{{ errorMessage }}</div>
      }
      <div class="inline-captcha-row">
        <re-captcha (resolved)="captchaEvents($event)" />
      </div>
      <tidy-button
        [text]="'Save'"
        [action]="boundSubmitCard"
        class="primary"
        [disabled]="submitting" />
    } @else {
      <div class="inline-payment-success">
        <img src="assets/svg/checkmark-circle.svg" class="inline-success-icon" />
        <span class="inline-success-text">
          @if (cardLast4) {
            Card ending in {{ cardLast4 }} saved!
          } @else {
            Payment method saved!
          }
        </span>
      </div>
    }
  `,
})
export class InlinePaymentFormComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('cardInfo') cardInfo: ElementRef;
  @Input() uuid = '';
  @Input() sharedLink: SharedLinkResponse;
  @Output() paymentSuccess = new EventEmitter<void>();

  errorMessage = '';
  hasValidCaptcha: string | null = null;
  submitting = false;
  success = localStorage.getItem('hasCard') === 'true';
  cardLast4 = '';
  isGuestMode = false;
  submitted = false;
  loaded = this.success;
  stripeReady = signal(false);

  boundSubmitCard = this.submitCard.bind(this);

  private readonly cd = inject(ChangeDetectorRef);
  private readonly fb = inject(FormBuilder);
  private readonly auth = inject(Auth);
  private readonly client = inject(Client);
  private readonly me = inject(Me);
  private readonly events = inject(Events);
  private readonly storage = inject(TidyStorage);
  private readonly guestAccountService = inject(GuestAccountService);
  private readonly posthogService = inject(PosthogService);
  private readonly bookService = inject(BookService);
  private readonly card = inject(Card);
  private readonly stripeProvider = inject(StripeProvider);
  private readonly sharedLinkService = inject(SharedLinkService);

  accountForm = this.fb.group({
    firstName: ['', [Validators.required, Validators.minLength(2)]],
    lastName: ['', [Validators.required, Validators.minLength(2)]],
    email: ['', [Validators.required, validateEmail]],
    password: ['', [Validators.required, Validators.minLength(8)]],
  });

  private cardEl: any = null;
  private cardHandler = this.onChange.bind(this);

  async ngOnInit(): Promise<void> {
    this.isGuestMode = await this.storage.retrieve('isGuestAccount') ?? false;
    if (this.isGuestMode) {
      this.success = false;
    } else if (!this.success) {
      const hasCard = await this.card.hasValidCard();
      if (hasCard) {
        this.success = true;
        const cards = await this.card.getCreditCards();
        this.cardLast4 = cards?.[0]?.metadata?.last4 || '';
      }
    } else {
      const cards = await this.card.getCreditCards();
      this.cardLast4 = cards?.[0]?.metadata?.last4 || '';
    }
    this.loaded = true;
    this.cd.detectChanges();
    if (!this.success) {
      this.initiateCardElement();
    }
  }

  ngAfterViewInit(): void {
    if (this.success || !this.loaded) {
      return;
    }
    this.initiateCardElement();
  }

  ngOnDestroy(): void {
    if (this.cardEl) {
      this.cardEl.removeEventListener('change', this.cardHandler);
      if (!this.cardEl._destroyed) {
        this.cardEl.destroy();
      }
      this.cardEl = null;
    }
  }

  private initiateCardElement(): void {
    this.stripeReady.set(false);
    if (this.cardEl) {
      this.cardEl.removeEventListener('change', this.cardHandler);
      if (!this.cardEl._destroyed) {
        this.cardEl.destroy();
      }
      this.cardEl = null;
    }
    setTimeout(() => {
      this.cardEl = this.stripeProvider.getCardElement();
      this.cardEl.mount(this.cardInfo.nativeElement);
      this.cardEl.addEventListener('change', this.cardHandler);
      this.cardEl.on('ready', () => {
        this.stripeReady.set(true);
        this.cd.detectChanges();
      });
    }, 100);
  }

  private onChange(err: any): void {
    this.errorMessage = err.error ? err.error.message : '';
    this.cd.detectChanges();
  }

  captchaEvents(captchaResult: string | null): void {
    this.hasValidCaptcha = captchaResult;
  }

  async submitCard(): Promise<void> {
    this.errorMessage = '';

    if (this.isGuestMode) {
      this.submitted = true;
      if (!this.accountForm.valid) {
        return;
      }
    }

    if (!this.hasValidCaptcha) {
      this.errorMessage = 'Please check that you are not a robot.';
      return;
    }

    this.submitting = true;
    try {
      if (this.isGuestMode) {
        await this.convertGuestAccount();
        this.isGuestMode = false;
      }

      const { token, error } = await this.stripeProvider
        .getInstance()
        .createToken(this.cardEl);

      if (error) {
        this.errorMessage = error.message;
        return;
      }

      await this.sharedLinkService.executeAction(
        this.uuid,
        'add_payment_method',
        { stripe_card_token: token.id }
      );
      this.cardLast4 = token.card?.last4 || '';
      this.success = true;
      localStorage.setItem('hasCard', 'true');
      this.paymentSuccess.emit();
    } catch (err: any) {
      this.errorMessage =
        err?.message || err?.error?.message || 'Something went wrong. Please try again.';
    } finally {
      this.submitting = false;
      this.cd.detectChanges();
    }
  }

  private async convertGuestAccount(): Promise<void> {
    const formData = this.accountForm.value;

    const payload = {
      profile: {
        first_name: formData.firstName!.trim(),
        last_name: formData.lastName!.trim(),
        email: formData.email!,
      },
    };
    await this.client.saveClientSettings(payload);

    const guestEmail = this.guestAccountService.generateGuestEmail();
    await this.client.saveClientSettings({ profile: { email: guestEmail } });

    const passwordPayload = {
      password: formData.password,
      confirm_password: formData.password,
      current_password: 'GuestPass123!',
    };
    await this.auth.changePassword(passwordPayload);
    await this.client.saveClientSettings(payload);

    localStorage.setItem('email', formData.email!);
    localStorage.setItem('firstName', formData.firstName!);
    localStorage.setItem('lastName', formData.lastName!);
    await this.storage.save('isGuestAccount', false);
    await this.storage.delete('me');
    const account = await this.me.load(true);
    if (account?.customer_account?.id && account?.customer_account?.email) {
      await this.posthogService.identify(
        account.customer_account.id,
        account.customer_account.email,
        { account_type: account.customer_account.account_type }
      );
      await this.posthogService.reloadFeatureFlags();
      await this.bookService.cacheBookingViewV2Variant();
    }
    this.auth.authenticated.set(true);
    this.events.publish('auth:complete');
  }
}
